import { useState, useRef, useCallback } from 'react';
import { Mic, Square, Trash2 } from 'lucide-react';
import { useDialog } from '../../../components/DialogProvider';

const MIME_PREFERIDO = 'audio/webm;codecs=opus';
const MIME_FALLBACK = 'audio/webm';

interface Props {
  onEnviar: (blob: Blob) => void;
}

export function GravadorAudio({ onEnviar }: Props) {
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const canceladoRef = useRef(false);
  const dialog = useDialog();

  const iniciar = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported(MIME_PREFERIDO) ? MIME_PREFERIDO : MIME_FALLBACK;
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (canceladoRef.current) {
          chunksRef.current = [];
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        onEnviar(blob);
      };

      recorder.start();
      canceladoRef.current = false;
      setGravando(true);
      setSegundos(0);
      timerRef.current = setInterval(() => setSegundos((s) => s + 1), 1000);
    } catch (err) {
      const nome = (err as Error).name;
      if (nome === 'NotAllowedError' || nome === 'PermissionDeniedError') {
        await dialog.alerta('Microfone bloqueado pelo navegador.\n\nClique no cadeado na barra de endereço e permita o acesso ao microfone para este site.');
      } else if (nome === 'NotFoundError') {
        await dialog.alerta('Nenhum microfone encontrado. Verifique se o dispositivo está conectado.');
      } else {
        await dialog.alerta('Não foi possível acessar o microfone. Verifique as permissões do navegador e tente novamente.');
      }
    }
  }, [onEnviar, dialog]);

  const parar = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setGravando(false);
  }, []);

  const cancelar = useCallback(() => {
    canceladoRef.current = true;
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setGravando(false);
  }, []);

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (gravando) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={cancelar}
          title="Cancelar gravacao"
          className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-lg transition"
        >
          <Trash2 size={20} />
        </button>
        <button
          onClick={parar}
          title="Enviar audio"
          className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
        >
          <Square size={14} fill="white" />
          <span className="text-sm font-mono">{fmt(segundos)}</span>
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={iniciar}
      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
      title="Gravar audio"
    >
      <Mic size={20} />
    </button>
  );
}
